import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { FileLink, ShipmentBatchView } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import {
  BATCH_LABELS_LABEL,
  BATCH_STATUS_LABEL,
  BATCH_STATUS_TONE,
  SHIPMENT_STATUS_TONE,
  kgLabel,
  providerLabel,
  statusLabel,
} from "./labels";

/**
 * Remessa de postagem (T3.4): objetos, retirar objeto (aberta), fechar (Correios: gera as etiquetas num PDF pela
 * fila), lista de postagem para levar à agência e etiquetas da remessa.
 */
export function BatchPage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmClose, setConfirmClose] = useState(false);
  const [closeKey, setCloseKey] = useState(() => crypto.randomUUID());
  const key = ["logistics", companyId, "batch", id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api<ShipmentBatchView>(`/logistics/batches/${id}`),
    refetchInterval: (q) => (q.state.data?.labelsStatus === "pending" ? 2000 : false),
  });
  const done = (b: ShipmentBatchView) => {
    qc.setQueryData(key, b);
    void qc.invalidateQueries({ queryKey: ["logistics", companyId] });
  };
  const onError = (e: Error) => toast("danger", errorMessage(e));
  const open = (path: string) => () => api<FileLink>(path);
  const manifest = useMutation({
    mutationFn: open(`/logistics/batches/${id}/manifest`),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener"),
    onError,
  });
  const labels = useMutation({
    mutationFn: open(`/logistics/batches/${id}/labels`),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener"),
    onError,
  });
  const close = useMutation({
    mutationFn: () =>
      api<ShipmentBatchView>(`/logistics/batches/${id}/close`, {
        method: "POST",
        headers: { "Idempotency-Key": closeKey },
        body: "{}",
      }),
    onSuccess: (b) => {
      setCloseKey(crypto.randomUUID());
      toast("success", `Remessa ${b.number} fechada`);
      done(b);
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (shipmentId: string) =>
      api<ShipmentBatchView>(`/logistics/batches/${id}/shipments`, {
        method: "POST",
        body: JSON.stringify({ remove: [shipmentId] }),
      }),
    onSuccess: done,
    onError,
  });
  const retryLabels = useMutation({
    mutationFn: () =>
      api<ShipmentBatchView>(`/logistics/batches/${id}/labels/retry`, { method: "POST", body: "{}" }),
    onSuccess: done,
    onError,
  });
  const b = query.data;
  const canUpdate = can(perms, "logistics", "update");
  const isOpen = b?.status === "open";
  return (
    <>
      <ContentHeader
        title={b ? `Remessa ${b.number}` : "Remessa de postagem"}
        breadcrumb={["Vendas", "Logística", "Remessas", b ? String(b.number) : ""]}
        actions={
          b && (
            <>
              <Link to="/vendas/logistica/remessas" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {!isOpen && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={manifest.isPending}
                  onClick={() => manifest.mutate()}
                >
                  Lista de postagem
                </button>
              )}
              {(b.provider === "melhorenvio" ? b.shipmentsCount > 0 : b.labelsStatus === "ready") && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={labels.isPending}
                  onClick={() => labels.mutate()}
                >
                  Imprimir etiquetas
                </button>
              )}
              {canUpdate && b.labelsStatus === "error" && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={retryLabels.isPending}
                  onClick={() => retryLabels.mutate()}
                >
                  Gerar etiquetas de novo
                </button>
              )}
              {canUpdate && isOpen && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={close.isPending || b.shipmentsCount === 0}
                  onClick={() => setConfirmClose(true)}
                >
                  Fechar remessa
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={query.isLoading} error={query.error} />
      {b && (
        <>
          {b.labelsStatus === "error" && b.labelsError && (
            <div className="alert alert-danger" role="alert">
              {b.labelsError}
            </div>
          )}
          <div className="box card p-3 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <StatusBadge tone={BATCH_STATUS_TONE[b.status]}>{BATCH_STATUS_LABEL[b.status]}</StatusBadge>
              <span>
                {b.integrationName} ({providerLabel(b.provider)})
              </span>
              <span data-testid="batch-summary">
                {b.shipmentsCount} {b.shipmentsCount === 1 ? "objeto" : "objetos"} · {kgLabel(b.weightG)} ·
                declarado {formatBRL(b.declaredValueCents)}
              </span>
              {b.labelsStatus !== "none" && (
                <span className="small text-muted">{BATCH_LABELS_LABEL[b.labelsStatus]}</span>
              )}
              {b.closedAt && <span className="small text-muted">Fechada em {dateTimeBR(b.closedAt)}</span>}
            </div>
            {b.notes && <p className="small mb-0 mt-2">{b.notes}</p>}
          </div>
          <div className="box card">
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Objetos da remessa">
                <thead>
                  <tr>
                    <th scope="col">Objeto</th>
                    <th scope="col">Destinatário</th>
                    <th scope="col">Serviço</th>
                    <th scope="col">Rastreio</th>
                    <th scope="col">Situação</th>
                    {isOpen && canUpdate && (
                      <th scope="col">
                        <span className="visually-hidden">Ações</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {b.shipments.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link to="/vendas/logistica/$id" params={{ id: s.id }}>
                          {s.number}
                        </Link>
                        {s.salesOrderNumber !== null && (
                          <div className="small text-muted">Pedido {s.salesOrderNumber}</div>
                        )}
                      </td>
                      <td>
                        {s.recipientName}
                        <div className="small text-muted">
                          {s.recipientCity}/{s.recipientUf} · {kgLabel(s.weightG)}
                        </div>
                      </td>
                      <td>{s.serviceName}</td>
                      <td className="font-monospace small">{s.trackingCode ?? "—"}</td>
                      <td>
                        <StatusBadge tone={SHIPMENT_STATUS_TONE[s.status]}>
                          {statusLabel(s.status)}
                        </StatusBadge>
                      </td>
                      {isOpen && canUpdate && (
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(s.id)}
                          >
                            Retirar
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <ConfirmDialog
        open={confirmClose}
        title="Fechar remessa"
        message={
          b?.provider === "correios"
            ? "Depois de fechada, a remessa não aceita novos objetos. As etiquetas são geradas num PDF só e a lista de postagem fica disponível para levar à agência."
            : "Depois de fechada, a remessa não aceita novos objetos e a lista de postagem fica disponível."
        }
        confirmLabel="Fechar remessa"
        tone="primary"
        onConfirm={() => {
          setConfirmClose(false);
          close.mutate();
        }}
        onCancel={() => setConfirmClose(false)}
      />
    </>
  );
}
